/**
 * BootstrapContext
 * 
 * Manages the post-login loading state and monitors bootstrap queries.
 * Controls when the PostLoginLoader should be visible.
 * 
 * Logic:
 * - startPostLogin() is called immediately after successful login
 * - finishPostLogin() is called when:
 *   1. All critical bootstrap queries are settled (success or error), AND
 *   2. Minimum duration (1200ms) has passed
 * 
 * The loader stays visible until both conditions are met.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useHomeBootstrapStatus } from '../hooks/useHomeBootstrapStatus';
import { useParams, useLocation } from 'react-router-dom';
import { getCompanySlugFromLocation } from '../utils/companySlug';
import { perfLog } from '../utils/performance';

export type BootstrapContextValue = {
  isPostLoginLoading: boolean;
  startPostLogin: () => void;
  finishPostLogin: () => void;
};

interface BootstrapContextType extends BootstrapContextValue {
  hasJustLoggedIn: boolean;
}

const BootstrapContext = createContext<BootstrapContextType | undefined>(undefined);

const MIN_DURATION_MS = 1200; // Minimum duration for loader (1.2 seconds)

export const BootstrapProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [bootStartedAt, setBootStartedAt] = useState<number | null>(null);
  const { company: paramCompany } = useParams<{ company?: string }>();
  const location = useLocation();
  
  // 🔥 CRITICAL: Get company from URL directly (more reliable than useParams)
  // This ensures we have the company slug even if useParams doesn't work
  const companyFromUrl = getCompanySlugFromLocation();
  const company = paramCompany || companyFromUrl || undefined;
  
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Monitor all bootstrap queries using centralized hook
  const { isLoading: isBootstrapQueriesLoading } = useHomeBootstrapStatus(company);

  // Fallback: If loader started but queries haven't started loading yet, 
  // wait a bit before considering them "not loading"
  // This handles race conditions where queries haven't initialized yet
  const [queriesInitialized, setQueriesInitialized] = useState(false);
  
  useEffect(() => {
    if (bootStartedAt && !queriesInitialized) {
      // Give queries 200ms to initialize
      const timer = setTimeout(() => {
        setQueriesInitialized(true);
      }, 200);
      return () => clearTimeout(timer);
    } else if (!bootStartedAt) {
      setQueriesInitialized(false);
    }
  }, [bootStartedAt, queriesInitialized]);

  // Calculate time passed since boot started
  const [timePassed, setTimePassed] = useState(0);

  // Update time passed every 500ms when boot is active (reduced from 100ms to reduce violations)
  // 500ms is still smooth enough for UI updates while significantly reducing timer overhead
  useEffect(() => {
    if (!bootStartedAt) {
      setTimePassed(0);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Update immediately
    setTimePassed(Date.now() - bootStartedAt);

    // Then update every 500ms (reduced from 100ms to reduce [Violation] 'setInterval' warnings)
    intervalRef.current = setInterval(() => {
      setTimePassed(Date.now() - bootStartedAt);
    }, 500);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [bootStartedAt]);

  // Auto-finish when conditions are met
  useEffect(() => {
    if (!bootStartedAt) return;

    // CRITICAL: Also check if token exists - if token is missing, finish immediately
    // This prevents infinite loading if token was cleared
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    if (!token) {
      perfLog.debug('[BootstrapContext] Token missing, finishing loader early');
      setBootStartedAt(null);
      setTimePassed(0);
      return;
    }

    const shouldFinish = 
      !isBootstrapQueriesLoading && 
      queriesInitialized &&
      timePassed >= MIN_DURATION_MS;

    if (shouldFinish) {
      // Small delay to ensure smooth transition
      const timer = setTimeout(() => {
        setBootStartedAt(null);
        setTimePassed(0);
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [bootStartedAt, isBootstrapQueriesLoading, queriesInitialized, timePassed]);

  const startPostLogin = useCallback(() => {
    const now = Date.now();
    setBootStartedAt(now);
    setTimePassed(0);
    perfLog.debug('[BootstrapContext] Post-login loader started', { timestamp: now });
  }, []);

  const finishPostLogin = useCallback(() => {
    perfLog.debug('[BootstrapContext] Post-login loader finished manually');
    setBootStartedAt(null);
    setTimePassed(0);
  }, []);

  // Computed: is post-login loading active?
  // Show loader if:
  // 1. Boot has started, AND
  // 2. We're on Home page (not other pages like Clients, Dashboard, etc.), AND
  // 3. (Queries are loading OR time hasn't passed minimum duration OR queries haven't initialized yet)
  // IMPORTANT: Only show PostLoginLoader on Home page, never on Clients or other pages
  const isHomePage = company 
    ? (location.pathname === `/${company}` || 
       location.pathname === `/${company}/` || 
       location.pathname === `/${company}/home` ||
       location.pathname.endsWith('/home'))
    : false;
  const isPostLoginLoading = 
    !!bootStartedAt && 
    isHomePage && // Only show loader on Home page, not on Clients or other pages
    (isBootstrapQueriesLoading || !queriesInitialized || timePassed < MIN_DURATION_MS);

  // Check if user just logged in (from sessionStorage)
  // Also check timestamp to ensure it's recent (within 30 seconds)
  const hasJustLoggedIn = (() => {
    if (typeof window === 'undefined') return false;
    const justLoggedIn = sessionStorage.getItem('justLoggedIn') === 'true';
    const justLoggedInTime = sessionStorage.getItem('justLoggedInTime');
    
    if (!justLoggedIn || !justLoggedInTime) return false;
    
    // Check if login was recent (within last 30 seconds)
    const timeSinceLogin = Date.now() - parseInt(justLoggedInTime, 10);
    return timeSinceLogin < 30000; // 30 seconds window
  })();

  // Auto-start if user just logged in and we haven't started yet
  // This handles the case where Login.tsx uses window.location.replace (full page reload)
  useEffect(() => {
    // Skip if already started or no company
    if (bootStartedAt || !company || company === 'undefined') {
      return;
    }

    // Check sessionStorage directly (don't rely on hasJustLoggedIn which is computed once)
    const checkJustLoggedIn = () => {
      if (typeof window === 'undefined') return false;
      const justLoggedIn = sessionStorage.getItem('justLoggedIn') === 'true';
      const justLoggedInTime = sessionStorage.getItem('justLoggedInTime');
      
      // Check if login was recent (within last 30 seconds)
      if (justLoggedIn && justLoggedInTime) {
        const timeSinceLogin = Date.now() - parseInt(justLoggedInTime, 10);
        return timeSinceLogin < 30000; // 30 seconds window
      }
      return false;
    };

    const shouldStart = checkJustLoggedIn();
    
    if (shouldStart) {
      // CRITICAL: Wait a bit to ensure token is saved and axios headers are set
      // This prevents "invalid token" errors when queries start
      const timer = setTimeout(() => {
        // Double-check token exists before starting loader
        const token = localStorage.getItem('token');
        if (!token) {
          perfLog.debug('[BootstrapContext] Token not found, skipping loader start');
          return;
        }

        // CRITICAL: Set token in both axios and apiClient before starting queries
        try {
          // Set in axios (used by some components)
          const axios = require('axios');
          if (axios.defaults.headers.common.Authorization !== `Bearer ${token}`) {
            axios.defaults.headers.common.Authorization = `Bearer ${token}`;
          }

          // Set in apiClient (used by most API calls)
          const { apiClient } = require('../api/apiClient');
          if (apiClient && apiClient.defaults) {
            apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          }
        } catch (err) {
          perfLog.debug('[BootstrapContext] Could not set axios headers:', err);
          // Continue anyway - interceptor should handle it
        }

        // Now start the loader
        startPostLogin();
        perfLog.debug('[BootstrapContext] Auto-started post-login loader');
      }, 150); // Small delay to ensure token is ready
      
      return () => clearTimeout(timer);
    }
  }, [bootStartedAt, company, startPostLogin]);

  // Clear justLoggedIn flag after loader finishes (to prevent re-showing on navigation)
  useEffect(() => {
    if (!isPostLoginLoading && bootStartedAt === null) {
      // Clear the flag after a short delay to ensure smooth transition
      const timer = setTimeout(() => {
        if (typeof window !== 'undefined') {
          sessionStorage.removeItem('justLoggedIn');
          sessionStorage.removeItem('justLoggedInCompany');
          sessionStorage.removeItem('justLoggedInTime');
          sessionStorage.removeItem('bootstrapStartTime');
          perfLog.debug('[BootstrapContext] Cleared justLoggedIn flags');
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isPostLoginLoading, bootStartedAt]);

  const value: BootstrapContextType = {
    hasJustLoggedIn,
    startPostLogin,
    finishPostLogin,
    isPostLoginLoading,
  };

  return (
    <BootstrapContext.Provider value={value}>
      {children}
    </BootstrapContext.Provider>
  );
};

export const useBootstrap = (): BootstrapContextType => {
  const context = useContext(BootstrapContext);
  if (context === undefined) {
    throw new Error('useBootstrap must be used within a BootstrapProvider');
  }
  return context;
};

